// Geometry classification — Detection Engine System 1 (spec §2.3–2.8).
//
// Несущая `c` → geometry_class (12 классов, priority-ordered narrowest-first,
// первое совпадение выигрывает) + level_band (для plateau) + forward-проекция.
// Edge cases N<3 (§2.7). Catch-all `irregular` для A≥2 без формы (§2.3 HIGH 2).

import { carrierFeatures, sumDownIn, sumUpIn } from './features.js';
import { THRESHOLDS as T } from './thresholds.js';

/**
 * @param c          несущая (красная линия)
 * @param fractional дробная ли несущая (переключает ε, §2.2)
 * @returns { geometryClass, levelBand, forward, features }
 */
export function classifyGeometry(c, fractional = false) {
  // Reading by answered (§2.3, s443.d5 Фаза 2): null (carrier-дырка на нетронутом этапе)
  // НИКОГДА не идёт в класс/чтение — сжимаем несущую до non-null подпоследовательности и
  // классифицируем форму отвеченных точек. На all-touched несущей нулов нет → cc === c
  // (byte-parity). Полностью пустая несущая (каждый этап — дырка) → вырожденный plateau/mid.
  const cc = c.filter((x) => x != null);
  if (cc.length === 0) {
    return {
      geometryClass: 'plateau',
      levelBand: 'mid',
      forward: forwardProjection([0]),
      features: carrierFeatures([0], fractional),
    };
  }
  const f = carrierFeatures(cc, fractional);
  const picked = pickClass(f);
  // Post-classifier: атомизируем catch-all 'irregular' на way-back /
  // high-held-collapse / residual irregular (s281). Pure addition — 12
  // типизированных классов сюда не доходят, риск регрессии = 0 by architecture.
  const geometryClass = picked === 'irregular' ? refineIrregular(cc) : picked;
  return {
    geometryClass,
    levelBand: levelBand(cc),
    forward: forwardProjection(cc),
    features: f,
  };
}

function pickClass(f) {
  const { N, c, amplitude, eMax, eMin, extremaMax, extremaMin, deltas, spans } = f;
  const A_OK = amplitude >= T.MOVEMENT_MIN_AMP;

  // plateau-доминанта: A ≤ 1 ⇒ plateau (§2.4), кроме N=1 (тоже plateau).
  if (amplitude <= T.PLATEAU_MAX_AMP) return 'plateau';

  // 1 — oscillation (N≥6): ≥4 чередующихся экстремума, каждый разворот ≥2.
  if (N >= 6 && eMax >= 2 && eMin >= 2 && A_OK && reversalsClear(c, extremaMax, extremaMin))
    return 'oscillation';

  // 2 — double-peak (N≥5): max–min–max, провал ≥2 (центральная яма на ≥2 ниже
  // нижнего из двух пиков — иначе это не два пика, а рябь).
  if (N >= 5 && eMax === 2 && eMin === 1 && A_OK) {
    const dip = Math.min(c[extremaMax[0]], c[extremaMax[1]]) - c[extremaMin[0]];
    if (dip >= T.MOVEMENT_MIN_AMP) return 'double-peak';
  }

  // 3 — double-valley (N≥5): min–max–min, подъём ≥2 (центральный пик на ≥2 выше
  // верхней из двух ям).
  if (N >= 5 && eMin === 2 && eMax === 1 && A_OK) {
    const rise = c[extremaMax[0]] - Math.max(c[extremaMin[0]], c[extremaMin[1]]);
    if (rise >= T.MOVEMENT_MIN_AMP) return 'double-valley';
  }

  // 4 — V-shape (N≥3): один interior min, концы близко, провал ≥2.
  if (N >= 3 && eMin === 1 && eMax === 0) {
    const trough = Math.min(...c.slice(1, -1));
    if (
      Math.abs(c[N - 1] - c[0]) <= T.VSHAPE_NET_TOL &&
      trough <= Math.min(c[0], c[N - 1]) - T.VSHAPE_DROP
    )
      return 'V-shape';
  }

  // 5 — peak-then-fall (N≥3): один interior max, рост→пик→падение, хвост падает.
  if (N >= 3 && eMax === 1 && eMin === 0) {
    const p = extremaMax[0];
    const rise = sumUpIn(deltas, 0, p);
    const fall = sumDownIn(deltas, p, N - 1);
    if (rise >= 1 && fall >= 1 && c[N - 1] < c[p]) return 'peak-then-fall';
  }

  // 6 — late-cliff (N≥4): hold ≥ HOLD_MIN на голове → обрыв в LATE.
  if (N >= 4) {
    const headStages = N - spans.late;
    if (
      headStages >= spans.holdMin &&
      regionAmp(c, 0, headStages) <= T.PARALLEL_MAX_GAP &&
      sumDownIn(deltas, N - 1 - spans.late, N - 1) >= T.MOVEMENT_MIN_AMP
    )
      return 'late-cliff';

    // 7 — late-rise: hold → подъём в LATE.
    if (
      headStages >= spans.holdMin &&
      regionAmp(c, 0, headStages) <= T.PARALLEL_MAX_GAP &&
      sumUpIn(deltas, N - 1 - spans.late, N - 1) >= T.MOVEMENT_MIN_AMP
    )
      return 'late-rise';

    // 8 — early-cliff: обрыв в EARLY → низкое плато.
    const tailStages = N - spans.early;
    if (
      tailStages >= spans.holdMin &&
      regionAmp(c, spans.early, N) <= T.PARALLEL_MAX_GAP &&
      sumDownIn(deltas, 0, spans.early) >= T.MOVEMENT_MIN_AMP
    )
      return 'early-cliff';

    // 9 — early-rise: подъём в EARLY → высокое плато.
    if (
      tailStages >= spans.holdMin &&
      regionAmp(c, spans.early, N) <= T.PARALLEL_MAX_GAP &&
      sumUpIn(deltas, 0, spans.early) >= T.MOVEMENT_MIN_AMP
    )
      return 'early-rise';
  }

  // 10 — monotonic-descent: только down/flat, ≥1 down, покрывает ≥ MAJORITY_SPAN.
  if (isMonotonic(f, 'down')) return 'monotonic-descent';
  // 11 — monotonic-ascent.
  if (isMonotonic(f, 'up')) return 'monotonic-ascent';

  // 12 — plateau уже отловлен сверху (A ≤ 1).
  // Catch-all (§2.3 HIGH 2): A ≥ 2 без формы → irregular.
  return 'irregular';
}

/**
 * Post-detection refine для catch-all 'irregular' (s281). Вызывается ТОЛЬКО на
 * остатке pickClass() === 'irregular' (12 типизированных классов сюда не
 * доходят). Возвращает одно из: 'way-back' | 'high-held-collapse' | 'irregular'.
 *
 * Порядок: way-back сначала (подвыборка больше, short-circuit эффективнее).
 * Результат от порядка не зависит — фильтры взаимоисключаются по argmin position
 * (s281.d6 Q-irregular-refine-order resolved).
 */
export function refineIrregular(values) {
  if (matchesWayBack(values)) return 'way-back';
  if (matchesHighHeldCollapse(values)) return 'high-held-collapse';
  return 'irregular'; // residual
}

/**
 * way-back: был значимый провал + значимый подъём из ямы + минимум не на финале
 * + провал ЛЕЖИТ НИЖЕ СТАРТА больше шумового пола (s509.d4).
 * Final может всё ещё быть в минусе (incomplete recovery — это тоже way-back).
 *
 * s509.d4: старое условие исключало минимум только на финале, но не на старте, поэтому кривая,
 * начавшаяся в собственном минимуме, получала фразу «the line falls below where it began» —
 * про падение, которого не было ([-2,-2,2], argmin=0). CD предложил строгое `min < c[0]`;
 * замер показал, что строгое сравнение закрывает лишь равенство и оставляет волосок: на дробной
 * несущей [-0.6,-0.7,1.0] провал 0.10 всё ещё проходил. Отсюда сравнение с шумовым полом.
 *
 * Порог один на оба вида несущей: на целочисленной минимальный реальный провал = 1, то есть
 * `> 0.5` выполняется сам собой — ветка под EPS_INTEGER была бы мёртвой. Дробные несущие
 * (среднее со-параллельных слоёв: пары, тройки, поля) — обычный вход, поэтому дыра была
 * достижимой, а не теоретической.
 */
export function matchesWayBack(values) {
  const n = values.length;
  const min = Math.min(...values);
  const argmin = values.indexOf(min);
  return (
    min < -0.5 &&
    values[n - 1] - min > 1.5 &&
    argmin !== n - 1 &&
    values[0] - min > T.EPS_FRACTIONAL
  );
}

/**
 * high-held-collapse: была пара соседних высоких этапов («было хорошо и
 * держалось») + минимум именно на финале («и я это потерял») + финал реально
 * низкий.
 */
export function matchesHighHeldCollapse(values) {
  const n = values.length;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const argmin = values.indexOf(min);

  let hasHighHeldPair = false;
  for (let i = 0; i < n - 1; i++) {
    if (values[i] >= 2 && values[i + 1] >= 2) {
      hasHighHeldPair = true;
      break;
    }
  }

  const finalLow = values[n - 1] <= -1 || values[n - 1] <= max - 3;

  return hasHighHeldPair && argmin === n - 1 && finalLow;
}

/** Амплитуда на под-окне [from, to). */
function regionAmp(c, from, to) {
  const seg = c.slice(Math.max(0, from), Math.min(c.length, to));
  if (seg.length === 0) return 0;
  return Math.max(...seg) - Math.min(...seg);
}

/** Монотонность с edge-релаксацией N=2 (§2.7). */
function isMonotonic(f, dir) {
  const { steps, net, N, directedSegments, spans } = f;
  const opp = dir === 'down' ? 'up' : 'down';
  if (steps.includes(opp)) return false;
  if (!steps.includes(dir)) return false;
  const netOk = dir === 'down' ? net <= -T.MONOTONIC_MIN_NET : net >= T.MONOTONIC_MIN_NET;
  if (!netOk) return false;
  if (N === 2) return true; // §2.7 edge: single step допускается
  // покрытие ≥ MAJORITY_SPAN переходов одним направлением.
  const runLen = directedSegments.filter((d) => d === dir).length
    ? maxRun(steps, dir)
    : 0;
  return runLen >= spans.majoritySpan;
}

/** Длина самого длинного run шагов направления dir (flat поглощается). */
function maxRun(steps, dir) {
  let best = 0;
  let cur = 0;
  for (const s of steps) {
    if (s === dir || s === 'flat') {
      if (s === dir || cur > 0) cur += 1; // flat внутри run считается
    } else {
      best = Math.max(best, cur);
      cur = 0;
    }
  }
  return Math.max(best, cur);
}

/** Чёткость разворотов для oscillation: соседние экстремумы различаются ≥2. */
function reversalsClear(c, extremaMax, extremaMin) {
  const idxs = [...extremaMax, ...extremaMin].sort((a, b) => a - b);
  for (let i = 1; i < idxs.length; i++)
    if (Math.abs(c[idxs[i]] - c[idxs[i - 1]]) < T.MOVEMENT_MIN_AMP) return false;
  return idxs.length >= 4;
}

/**
 * level_band (§2.5): band_metric = **текущее значение** несущей `c[N-1]` (Stage 5,
 * «где сейчас») на фикс −3..+3 шкале. Эмитится для ЛЮБОЙ несущей, не только
 * plateau. Current, НЕ mean: Life Map якорится на Stage 5; на rising-then-falling
 * несущей mean врёт (high mean + low current → копи должна говорить про low
 * current). Для plateau current ≈ mean, так что s247 banking совместим (расходятся
 * лишь рябящие плато на границе ±1.5 — current-value консистентнее).
 */
export function levelBand(c) {
  // Current = ПОСЛЕДНЯЯ отвеченная точка (null-дырки исключены, §2.5 / s443.d5 Ф2).
  const vals = c.filter((x) => x != null);
  const m = vals.length ? vals[vals.length - 1] : 0;
  if (m >= T.LEVEL_HIGH) return 'high';
  if (m <= T.LEVEL_LOW) return 'low';
  return 'mid';
}

/**
 * Forward-проекция (§2.8, редакция F4): 2 ветки, Shape_II НЕ НИЖЕ Shape_I. Анкер — ПОСЛЕДНЯЯ
 * точка переданной кривой (для §VII это силуэт = среднее active-слоёв, НЕ несущая —
 * иначе обе ветки уходят в сторону несущей, а не «две дороги» от твоей жизни, s384).
 * Shape I = продолжение тренда. Shape II = конец разворота E = min(+3, max(+1.5, P) + 0.35),
 * P — пик силуэта (канон F4, s980), но не ниже Shape I (`s981.d1`): после провала и резкого
 * отскока продолжение отскока уходит выше E — тогда линии сливаются. Форма линии — рендер.
 */
export function forwardProjection(c) {
  const N = c.length;
  const clamp = (x) => Math.max(-3, Math.min(3, x));
  // Пол +1.5 абсолютный (по шкале, не от последнего этапа) — пик-флор s385 живёт внутри max.
  const end = Math.min(3, Math.max(T.SHAPE_II_FLOOR, Math.max(...c)) + T.SHAPE_II_LIFT);
  const shapeI = N < 2 ? clamp(c[0] ?? 0) : clamp(c[N - 1] + (c[N - 1] - c[N - 2]));
  return { shapeI, shapeII: Math.max(end, shapeI) };
}
